import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";
import { isOpenStatus } from "@/domains/pm/statuses/statusCatalogue";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";

import {
  documentExamples,
  issueExamples,
  type DocumentExample,
  type DocumentMetric,
  type IssueExample,
} from "./examples";
import { buildForYouItems, type ForYouItem } from "./forYou";
import { fetchMyMentions, fetchRecentDocumentRows, fetchRecentIssueActivity } from "./repository";
import {
  summariseDocumentRows,
  summariseIssueActivity,
  windowMs,
  type DocumentChangeSummary,
  type IssueChangeSummary,
  type IssueMetric,
  type RecentChangeWindow,
} from "./summary";

/** How often the timeframe rolls forward. Rows age out without a refetch. */
const CLOCK_TICK_MS = 60_000;

/**
 * Coalescing window for the realtime refetch. Longer than the default because
 * a comment's mention rows are written by a SEPARATE request after the comment
 * (and the mention tables are not in the realtime publication): the comment's
 * own event must not refetch before its mentions exist.
 */
const REALTIME_WINDOW_MS = 2_000;

export const recentChangesKeys = {
  all: (buildingId: number, projectId: number) =>
    ["project-recent-changes", buildingId, projectId] as const,
  issues: (buildingId: number, projectId: number, window: RecentChangeWindow) =>
    ["project-recent-changes", buildingId, projectId, "issues", window] as const,
  documents: (buildingId: number, projectId: number, window: RecentChangeWindow) =>
    ["project-recent-changes", buildingId, projectId, "documents", window] as const,
  mentions: (
    buildingId: number,
    projectId: number,
    window: RecentChangeWindow,
    me: number | undefined,
    issues: boolean
  ) => ["project-recent-changes", buildingId, projectId, "mentions", window, me, issues] as const,
};

/** A clock that ticks once a minute while the dashboard is mounted. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export interface RecentChangesResult<T, E> {
  summary: T | undefined;
  /** The items behind each count, newest first (see `examples.ts`). */
  examples: E | undefined;
  isLoading: boolean;
  isError: boolean;
}

interface Options {
  buildingId: number;
  projectId: number;
  window: RecentChangeWindow;
  /** The viewer's `user_profiles.id`; "For you" is empty without it. */
  userId: number | undefined;
  /** The disciplines the viewer is responsible for in this project (Contacts). */
  myDisciplineIds: number[];
  /** Skip the issue half — no PM module, or no access to this project's issues. */
  issuesEnabled: boolean;
  /** Skip the document counts — the viewer does not see the whole project's documents. */
  documentsEnabled: boolean;
}

function useRecentChangeQueries(options: Options) {
  const { buildingId, projectId, window, userId, issuesEnabled, documentsEnabled } = options;
  const valid = buildingId > 0 && projectId > 0;
  const mentionsEnabled = valid && userId != null;
  useRealtimeInvalidate({
    enabled: valid,
    topic: realtimeTopic("project-recent-changes", buildingId, projectId),
    bindings: [
      ...(issuesEnabled
        ? [
            {
              event: "INSERT" as const,
              table: "pm_issue_activity",
              filter: `building_id=eq.${buildingId}`,
            },
          ]
        : []),
      { event: "INSERT", table: "documents", filter: `building_id=eq.${buildingId}` },
      // Comments on threads not filed as issues (their mentions).
      { event: "INSERT", table: "document_comments", filter: `building_id=eq.${buildingId}` },
    ],
    invalidate: [{ queryKey: recentChangesKeys.all(buildingId, projectId) }],
    windowMs: REALTIME_WINDOW_MS,
  });

  const since = () => new Date(Date.now() - windowMs(window)).toISOString();

  const issuesQuery = useQuery({
    queryKey: recentChangesKeys.issues(buildingId, projectId, window),
    enabled: valid && issuesEnabled,
    staleTime: 60_000,
    queryFn: () => fetchRecentIssueActivity(buildingId, projectId, since()),
  });

  const documentsQuery = useQuery({
    queryKey: recentChangesKeys.documents(buildingId, projectId, window),
    enabled: valid && documentsEnabled,
    staleTime: 60_000,
    queryFn: () => fetchRecentDocumentRows(buildingId, projectId, since()),
  });

  const mentionsQuery = useQuery({
    queryKey: recentChangesKeys.mentions(buildingId, projectId, window, userId, issuesEnabled),
    enabled: mentionsEnabled,
    staleTime: 60_000,
    queryFn: () => fetchMyMentions(projectId, userId as number, since(), { issues: issuesEnabled }),
  });

  return { issuesQuery, documentsQuery, mentionsQuery };
}

type SummaryInput = Pick<
  ReturnType<typeof useRecentChangeQueries>,
  "issuesQuery" | "documentsQuery"
> & {
  catalogueLoading: boolean;
  now: number;
  window: RecentChangeWindow;
  isDone: (key: string) => boolean;
};
function useChangeSummaries({
  issuesQuery,
  documentsQuery,
  catalogueLoading,
  now,
  window,
  isDone,
}: SummaryInput) {
  const issueSummary = useMemo(
    () =>
      issuesQuery.data && !catalogueLoading
        ? summariseIssueActivity(issuesQuery.data, now, window, isDone)
        : undefined,
    [issuesQuery.data, catalogueLoading, now, window, isDone]
  );

  const documentSummary = useMemo(
    () =>
      documentsQuery.data ? summariseDocumentRows(documentsQuery.data, now, window) : undefined,
    [documentsQuery.data, now, window]
  );

  const issueExampleLists = useMemo(
    () =>
      issuesQuery.data && !catalogueLoading
        ? issueExamples(issuesQuery.data.rows, now, window, isDone)
        : undefined,
    [issuesQuery.data, catalogueLoading, now, window, isDone]
  );

  const documentExampleLists = useMemo(
    () =>
      documentsQuery.data ? documentExamples(documentsQuery.data.rows, now, window) : undefined,
    [documentsQuery.data, now, window]
  );

  return {
    issues: {
      summary: issueSummary,
      examples: issueExampleLists,
      // The catalogue decides what "completed" means; counting before it
      // loads would read every custom done status as open.
      isLoading: issuesQuery.isLoading || catalogueLoading,
      isError: issuesQuery.isError,
    },
    documents: {
      summary: documentSummary,
      examples: documentExampleLists,
      isLoading: documentsQuery.isLoading,
      isError: documentsQuery.isError,
    },
  };
}

/**
 * What changed in one project over the viewer's chosen timeframe: aggregate
 * counts, and "For you" — mentions, assignments and new issues in the
 * viewer's disciplines.
 *
 * Each query caches the RAW rows for its timeframe (fetched from "now minus
 * the timeframe" at fetch time); everything shown is derived in render against
 * a ticking clock, so the window rolls forward between fetches and a row simply
 * ages out. Switching timeframe is a new cache entry, not a refilter — a month
 * is not read on every load just in case.
 *
 * Only INSERTs are bound: everything here is counted by creation time, so an
 * insert is the only event that adds to it. Documents in particular emit an
 * UPDATE per processing step, and binding those would refetch on every state
 * change of a bulk upload. Trashing a document or deleting an issue lowers a
 * count on the next refetch (window focus or the channel's resync).
 */
function personalizedLoadState(
  catalogueLoading: boolean,
  mentions: { isLoading: boolean; isError: boolean },
  issuesEnabled: boolean,
  issues: { isLoading: boolean; isError: boolean }
) {
  return {
    isLoading: catalogueLoading || mentions.isLoading || (issuesEnabled && issues.isLoading),
    isError: mentions.isError || (issuesEnabled && issues.isError),
  };
}

export interface ProjectRecentChanges {
  issues: RecentChangesResult<IssueChangeSummary, Record<IssueMetric, IssueExample[]>>;
  documents: RecentChangesResult<DocumentChangeSummary, Record<DocumentMetric, DocumentExample[]>>;
  forYou: { items: ForYouItem[]; isLoading: boolean; isError: boolean };
}

export function useProjectRecentChanges(options: Options): ProjectRecentChanges {
  const { projectId, window, issuesEnabled } = options;
  const now = useMinuteClock();
  const { catalogue, isLoading: catalogueLoading } = useStatusCatalogue();

  const { issuesQuery, documentsQuery, mentionsQuery } = useRecentChangeQueries(options);

  const isDone = useMemo(
    () => (key: string) => !isOpenStatus(catalogue, projectId, key),
    [catalogue, projectId]
  );

  const { issues, documents } = useChangeSummaries({
    issuesQuery,
    documentsQuery,
    catalogueLoading,
    now,
    window,
    isDone,
  });

  const forYouItems = usePersonalChanges(
    options,
    { issuesQuery, mentionsQuery },
    now,
    catalogueLoading,
    isDone
  );

  return {
    issues,
    documents,
    forYou: {
      items: forYouItems,
      ...personalizedLoadState(catalogueLoading, mentionsQuery, issuesEnabled, issuesQuery),
    },
  };
}

function usePersonalChanges(
  options: Options,
  queries: Pick<ReturnType<typeof useRecentChangeQueries>, "issuesQuery" | "mentionsQuery">,
  now: number,
  catalogueLoading: boolean,
  isDone: (key: string) => boolean
) {
  const { buildingId, projectId, window, userId, myDisciplineIds } = options;
  const { issuesQuery, mentionsQuery } = queries;
  return useMemo(
    () =>
      userId == null || catalogueLoading
        ? []
        : buildForYouItems({
            buildingId,
            projectId,
            me: userId,
            now,
            window,
            activity: issuesQuery.data?.rows ?? [],
            mentions: mentionsQuery.data,
            myDisciplineIds,
            isDone,
          }),
    [
      userId,
      catalogueLoading,
      buildingId,
      projectId,
      now,
      window,
      issuesQuery.data,
      mentionsQuery.data,
      myDisciplineIds,
      isDone,
    ]
  );
}
